import type { ZodType } from 'zod';

/**
 * Backend xato konvertining mijoz tomonidagi ko'rinishi.
 * `status === 0` — tarmoq umuman javob bermadi (server o'chiq, internet yo'q).
 */
export class ApiClientError extends Error {
  override name = 'ApiClientError';

  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface ApiClientOptions {
  /** Masalan `https://eombor.example/api/v1` — oxirida qiyshiq chiziqsiz. */
  baseUrl: string;
  getToken: () => string | null;
  /**
   * 401 kelganda BIR MARTA chaqiriladi: yangi token olishga urinish.
   * `true` qaytsa so'rov yangi token bilan qaytadan yuboriladi.
   */
  renewSession?: () => Promise<boolean>;
  /** Yangilash ham yordam bermaganda — sessiyani tozalash va login sahifasiga o'tish. */
  onUnauthorized?: () => void;
}

/**
 * Bu yo'llar 401 dan keyin QAYTA yuborilmaydi.
 *
 * `/auth/refresh` — yangilashning o'zi: uni yangilashdan keyin takrorlash
 * cheksiz halqa bo'lardi. `/auth/login` va `/auth/logout` esa 401 ni
 * MA'NOLI javob sifatida qaytaradi ("parol noto'g'ri"), tokenning
 * eskirgani sifatida emas.
 */
const NO_RETRY = ['/auth/login', '/auth/refresh', '/auth/logout'];

export interface ApiClient {
  apiFetch: <T>(path: string, init?: RequestInit & { schema?: ZodType<T> }) => Promise<T>;
  /**
   * Binar javob (PDF, Excel). Konvert ochilmaydi — server faylni
   * to'g'ridan-to'g'ri yozadi; xato esa baribir `{ error }` konvertida
   * keladi va shu yerda `ApiClientError` ga aylanadi.
   */
  apiBlob: (path: string, init?: RequestInit) => Promise<Blob>;
}

/**
 * `fetch` ustidagi yupqa qatlam: `{ data }` konvertini ochadi,
 * `{ error }` konvertini `ApiClientError` ga aylantiradi va javobni
 * ixtiyoriy Zod sxemasi bilan tekshiradi.
 */
export const createApiClient = (options: ApiClientOptions): ApiClient => {
  /*
   * Yangilash BIR MARTA ketadi.
   *
   * Sahifa ochilganda bir vaqtda o'nlab so'rov uchadi va token eskirgan
   * bo'lsa hammasi baravar 401 oladi. Har biri o'z yangilashini boshlasa,
   * server o'nta `refresh` ni ketma-ket ishlatib, oxirgisidan boshqa
   * hammasining tokenini bekor qilardi — ya'ni yangilashning o'zi
   * foydalanuvchini tizimdan chiqarib yuborardi.
   */
  let renewing: Promise<boolean> | null = null;
  const renew = (): Promise<boolean> => {
    renewing ??= (options.renewSession?.() ?? Promise.resolve(false))
      .catch(() => false)
      .finally(() => {
        renewing = null;
      });
    return renewing;
  };

  const retryable = (path: string, status: number): boolean =>
    status === 401 &&
    options.renewSession !== undefined &&
    !NO_RETRY.some((prefix) => path.startsWith(prefix));

  /** Ikki yo'l uchun umumiy so'rov: sarlavhalar va tarmoq xatosi bir joyda. */
  const send = async (path: string, init: RequestInit): Promise<Response> => {
    const { headers, ...rest } = init;
    const token = options.getToken();

    /*
     * `FormData` uchun `Content-Type` QO'YILMAYDI.
     *
     * Uni brauzerning o'zi yozadi va oxiriga `boundary` qo'shadi — bo'laklar
     * bir-biridan aynan shu bilan ajraladi. Qo'lda yozilsa, `boundary`
     * yo'qoladi va server tanani umuman o'qiy olmaydi.
     */
    const isForm = rest.body instanceof FormData;

    try {
      return await fetch(`${options.baseUrl}${path}`, {
        ...rest,
        headers: {
          ...(isForm ? {} : { 'Content-Type': 'application/json' }),
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
          ...(headers as Record<string, string> | undefined),
        },
      });
    } catch {
      throw new ApiClientError("Serverga ulanib bo'lmadi", 'INTERNAL', 0);
    }
  };

  const apiFetch = async <T>(
    path: string,
    init: RequestInit & { schema?: ZodType<T> } = {},
  ): Promise<T> => {
    const { schema, ...rest } = init;
    let response = await send(path, rest);

    // Token eskirgan bo'lishi mumkin — yangilab, so'rovni bir marta
    // takrorlaymiz. Tana satr yoki `FormData`, ikkalasi ham qayta yuboriladi.
    if (retryable(path, response.status) && (await renew())) {
      response = await send(path, rest);
    }

    const body: unknown =
      response.status === 204 ? null : await response.json().catch(() => null);

    if (!response.ok) {
      if (response.status === 401) options.onUnauthorized?.();
      const err = (body as { error?: { code?: string; message?: string; details?: unknown } } | null)
        ?.error;
      throw new ApiClientError(
        err?.message ?? 'Xatolik yuz berdi',
        err?.code ?? 'INTERNAL',
        response.status,
        err?.details,
      );
    }

    const data = (body as { data?: unknown } | null)?.data;
    if (schema === undefined) return data as T;

    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new ApiClientError(
        'Serverdan kutilmagan javob keldi',
        'INTERNAL',
        response.status,
        parsed.error.issues,
      );
    }
    return parsed.data;
  };

  const apiBlob = async (path: string, init: RequestInit = {}): Promise<Blob> => {
    let response = await send(path, init);
    if (retryable(path, response.status) && (await renew())) {
      response = await send(path, init);
    }

    if (!response.ok) {
      if (response.status === 401) options.onUnauthorized?.();
      // Xato javobi JSON: faylni kutgan joyda ham xabar odam o'qiydigan
      // matn bo'lib qolishi kerak, "0 bayt yuklandi" emas.
      const body: unknown = await response.json().catch(() => null);
      const err = (body as { error?: { code?: string; message?: string; details?: unknown } } | null)
        ?.error;
      throw new ApiClientError(
        err?.message ?? 'Xatolik yuz berdi',
        err?.code ?? 'INTERNAL',
        response.status,
        err?.details,
      );
    }

    return response.blob();
  };

  return { apiFetch, apiBlob };
};
